/* eslint-disable complexity */
import React from 'react';

import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';
import {AnimatePresence} from 'motion/react';

import {ChevronDown} from '../../../icons/ChevronDown';
import {Close} from '../../../icons/Close';
import {Info} from '../../../icons/Info';
import {P} from '../../../layout/Text/Text';
import {getPLineHeight, getPSize} from '../../../layout/Text/utils';
import {Button} from '../../Button/Button';
import {InfoPopup} from '../../InfoPopup/InfoPopup';
import {Hints} from '../Hints/Hints';
import {OptionItem} from '../OptionItem/OptionItem';
import {OptionList} from '../OptionList/OptionList';

import {useBaseDropdownInput} from './useBaseDropdownInput';
import {
    getBaseVariables,
    getDisabledVariables,
    getErrorVariables,
    getFocusVariables
} from './utils';

import type {Option} from '../../../../utils/childTypes';
import type {ValidationFunc} from '@nfq/react-form';

/**
 * The `ComponentProps` interface defines the properties for the `DropdownInput` component.
 * This interface specifies all the necessary props for creating a customizable dropdown select input
 * that allows users to choose from a list of predefined options with support for default values,
 * disabled states, and flexible positioning of the dropdown menu.
 */
export interface BaseDropdownProps {
    /**
     * The default value that should be pre-selected when the dropdown is first rendered.
     * This value should correspond to one of the option values in the options array to ensure
     * proper selection state initialization.
     */
    defaultValue?: string;
    /**
     * Indicates whether to retain the field value when the component unmounts.
     *
     * @default false
     */
    hasSearch?: boolean;
    /**
     * An array of hint messages to display below the dropdown input.
     * These hints provide additional context or guidance to the user about the expected input
     * or usage of the dropdown component.
     *
     * @default undefined
     */
    hints?: string[];
    /**
     * Optional informational text that provides additional context about the dropdown input.
     */
    infoText?: string;
    /**
     * The label for the select dropdown.
     */
    inputLabel: string;
    /**
     * Determines whether the dropdown input should be rendered in a disabled state.
     * When disabled, the dropdown becomes non-interactive and displays visual indicators
     * to show that it cannot be used for selection.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Indicates whether to retain the field value when the component unmounts.
     *
     * @default false
     */
    isKeepOnUnmount?: boolean;
    /**
     * The name identifier for the dropdown input field used for form handling and validation.
     * This property is essential for form submission and helps identify the field when processing
     * form data or tracking field-specific validation errors.
     */
    name: string;
    /**
     * Callback function that gets executed when a user selects an option from the dropdown.
     * The function receives the selected value and optionally the field name, allowing parent
     * components to handle state updates and form validation appropriately.
     */
    onChange?(value: string[] | number | string, name?: string): void;
    /**
     * An array of selectable options that will be displayed in the dropdown menu.
     * Each option must conform to the Option type structure with label and value properties
     * to ensure consistent rendering and selection behavior throughout the component.
     */
    options: Option[];
    /**
     * Optional placeholder text that appears when no option has been selected.
     * This provides users with context about what type of selection is expected and
     * improves the overall user experience by offering guidance on the field's purpose.
     */
    placeholder?: string;
    /**
     * Controls the horizontal positioning of the dropdown menu relative to the input field.
     * This property allows for flexible layout accommodation in different UI contexts where
     * space constraints may require the dropdown to appear in specific positions.
     *
     * @default 'right'
     */
    popOverPosition?: 'center' | 'left' | 'right';
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing scenarios
     * and enables reliable automated testing by providing a consistent selector target.
     *
     * @default 'DropdownInputInput'
     */
    testId?: string;
    /**
     * Optional validation function to validate the input value.
     */
    validation?: ValidationFunc;
}

/**
 * The `DropdownInputInput` component renders a customizable select input with options, which can be disabled and allows users to
 * choose an option from a list. The component also provides a popover list of options with animation and handles the user's selection
 * with the provided `onChange` callback.
 *
 * @param props                 The component props.
 * @param props.defaultValue    The default selected value for the select input.
 * @param props.hasSearch       Indicates whether the select input should include a search functionality to filter options.
 * @param props.isDisabled      Optional. Determines whether the select input is disabled. Defaults to `false`.
 * @param props.inputLabel      The label for the select dropdown.
 * @param props.onChange        The callback function to handle changes when the user selects a new option.
 * @param props.options         An array of options to be rendered in the select input, each having a label and value.
 * @param props.hints           Optional. An array of hint messages to display below the select input.
 * @param props.infoText        Optional. Informational text to provide additional context about the select input.
 * @param props.popOverPosition Optional. The position of the popover list that displays the options. Defaults to `'right'`.
 * @param props.testId          Optional. A unique identifier for testing purposes. Defaults to `'DropdownInputInput'`.
 * @param props.name            The name identifier for the form field, used for form validation and field tracking.
 * @param props.placeholder     Optional. Placeholder text to display when no option is selected.
 * @param props.isKeepOnUnmount Optional. Determines whether to keep the form field value when the component unmounts. Defaults to `false`.
 * @param props.validation      Optional validation function to validate the selected value.
 * @returns A JSX element representing the filter select input with animated popover options.
 *
 * @example
 * ```tsx
 * <Combobox
 *   defaultValue="Option1"
 *   onChange={handleSelectChange}
 *   options={[
 *     {label: 'Option1', value: '1'},
 *     {label: 'Option2', value: '2'}
 *   ]}
 *   popOverPosition="left"
 * />
 * ```
 */
const BaseDropdownInput = ({
    defaultValue = '',
    hasSearch = false,
    hints = [],
    infoText,
    inputLabel,
    isDisabled = false,
    isKeepOnUnmount = false,
    name,
    onChange = () => null,
    options,
    placeholder,
    popOverPosition = 'right',
    testId = 'BaseDropdownInput',
    validation = () => null
}: BaseDropdownProps) => {
    const {
        chosenOption,
        colors,
        error,
        filteredOptions,
        handleChangeSearchText,
        handleChangeValue,
        handleDeleteClicked,
        handleToggleSelect,
        isOpen,
        isTop,
        listHeight,
        searchValue,
        selectRef
    } = useBaseDropdownInput(name, defaultValue, hasSearch, options, onChange, isDisabled, isKeepOnUnmount, validation);

    return (
        <Wrapper
            ref={selectRef}
            data-cy={testId}
            data-disabled={isDisabled}
            data-has-error={error !== null && error.length > 0}
        >
            <LabelInfoButtonWrapper>
                <InputLabel data-cy={`${testId}-label`}>{inputLabel}</InputLabel>
                {infoText && (
                    <InfoPopup testId={`${testId}-info-popup`}>
                        <P $size="small" data-cy={`${testId}-info-text`}>{infoText}</P>
                    </InfoPopup>
                )}
            </LabelInfoButtonWrapper>
            <Spacer y={2} />
            <InputWrapper $isDisabled={isDisabled} data-cy={`${testId}-dropdown-opener`} tabIndex={0} onClick={handleToggleSelect}>
                <SearchInput
                    autoComplete="off"
                    data-cy={`${testId}-search-input`}
                    disabled={isDisabled}
                    name="dropdown-search-input"
                    placeholder={placeholder}
                    readOnly={!hasSearch}
                    type="text"
                    value={searchValue}
                    onChange={handleChangeSearchText}
                />
                {hasSearch && chosenOption !== '' && (
                    <DeleteButton
                        as="button"
                        data-cy={`${testId}-delete-button`}
                        icon={Close}
                        isDisabled={isDisabled}
                        variant="secondary"
                        onClick={handleDeleteClicked}
                    />
                )}
                {(error !== null && error.length > 0) && <Info color1={colors.error} testId={`${testId}-error-icon`} />}
                {!hasSearch && (
                    <StyledChevron $isOpen={isOpen} height={20} width={20} />
                )}
                <AnimatePresence mode="wait">
                    {isOpen && (
                        <OptionList
                            height={listHeight}
                            isOnTop={isTop}
                            offset={1}
                            position={popOverPosition}
                            testId={`${testId}-list`}
                            hasFullWidth
                        >
                            {filteredOptions.length > 0 && filteredOptions.map(({label, value}) => {
                                const isActive = chosenOption === label;

                                return (
                                    <OptionItem
                                        key={value}
                                        index={String(value)}
                                        isActive={isActive}
                                        label={label}
                                        testId={`${testId}-option`}
                                        isSelectable
                                        onClick={handleChangeValue}
                                    />
                                );
                            })}
                            {filteredOptions.length === 0 && (
                                <P data-cy={`${testId}-no-options`}>
                                    Keine Ergebnisse gefunden
                                </P>
                            )}
                        </OptionList>
                    )}
                </AnimatePresence>
            </InputWrapper>
            {(error !== null && error.length > 0) && (
                <Hints baseTestId={testId} hints={error} hintType="error" paddingLeft={0} />
            )}
            {(error === null || error.length === 0) && <Hints baseTestId={testId} hints={hints} paddingLeft={0} />}
        </Wrapper>
    );
};

BaseDropdownInput.displayName = 'BaseDropdownInput';

export {BaseDropdownInput};

const Wrapper = styled.div`
    ${getBaseVariables}

    &:focus-within {
        ${getFocusVariables}
    }

    &[data-has-error=true] {
        ${getErrorVariables}
    }

    &[data-disabled=true] {
        ${getDisabledVariables}
    }
`;

const LabelInfoButtonWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(2)};
`;

const InputLabel = styled(P)`
    color: var(--label-color);
    transition: color 0.3s ease-in-out;
`;

interface InputWrapperProps {
    $isDisabled: boolean;
}

const InputWrapper = styled.div<InputWrapperProps>`
    align-items: center;
    background-color: var(--chosen-option-background-color);
    border: 1px solid var(--chosen-option-border-color);
    border-radius: 4px;
    cursor: var(--cursor);
    display: flex;
    gap: ${spacing(3)};
    justify-content: end;
    padding-right: ${spacing(4)};
    position: relative;
`;

const SearchInput = styled.input`
    background-color: transparent;
    border: none;
    color: var(--chosen-option-font-color);
    cursor: var(--cursor);
    font-family: ${({theme}) => theme.fonts.OnAir};
    font-size: ${getPSize({$size: 'large'})};
    line-height: ${getPLineHeight({$size: 'large'})};
    outline: none;
    padding: ${spacing(3)} ${spacing(4)};
    width: 100%;

    &::placeholder {
        color: var(--chosen-option-placeholder-font-color);
        font-family: ${({theme}) => theme.fonts.OnAir};
        font-size: ${getPSize({$size: 'large'})};
    }
`;

const DeleteButton = styled(Button)`
    background-color: transparent;
    border: none;
    height: min-content;
    padding: 0;
    width: min-content;

    &:hover{
        background-color: transparent;
        border: none;
    }

    path[fill]:not([fill="transparent"]) {
        fill: var(--delete-icon-color);
    }
`;

interface ChevronDownProps {
    $isOpen: boolean;
}

const StyledChevron = styled(ChevronDown)<ChevronDownProps>`
    transform: ${({$isOpen}) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.3s ease-in-out;
    path {
        fill: var(--chevron-color);
    }
`;